import { useLingui } from "@lingui/react/macro";
import { Badge, Button } from "@rakazo/ui-web";
import { Plus } from "lucide-react";
import { IconTooltip } from "../../components/IconTooltip";
import type { RosterFilter } from "./space-sidebar";
import { ROSTER_FILTERS } from "./space-sidebar";

/** One tab per Space the user belongs to, each with how many of its bots need them. */
export function SpaceTabs({
  spaces,
  currentSpaceId,
  needsYou,
  onSelect,
  onCreate,
  className = "",
}: {
  spaces: readonly { id: string; name: string }[];
  currentSpaceId: string | null;
  needsYou: ReadonlyMap<string, number>;
  onSelect: (spaceId: string) => void;
  onCreate: () => void;
  className?: string;
}) {
  const { t } = useLingui();
  return (
    <nav
      aria-label={t`Spaces`}
      data-testid="space-tabs"
      className={`no-scrollbar flex min-w-0 shrink-0 items-center gap-2 overflow-x-auto ${className}`}
    >
      {spaces.map((space) => {
        const current = space.id === currentSpaceId;
        const count = needsYou.get(space.id) ?? 0;
        return (
          <Button
            key={space.id}
            type="button"
            variant={current ? "default" : "ghost"}
            aria-current={current ? "page" : undefined}
            aria-label={count > 0 ? t`${space.name}, ${count} need you` : space.name}
            data-space-tab={space.id}
            onClick={() => {
              if (!current) onSelect(space.id);
            }}
            className={`h-9 shrink-0 gap-1.5 px-3.5 ${current ? "" : "bg-sidebar text-foreground/80 hover:bg-sidebar-accent"}`}
          >
            <span className="max-w-[10rem] truncate" dir="auto">
              {space.name}
            </span>
            {count > 0 ? (
              <Badge
                aria-hidden="true"
                data-testid="space-tab-needs-you"
                className="h-4.5 min-w-4.5 bg-destructive-fill px-1 text-[10px] tabular-nums text-destructive-foreground"
              >
                {count > 99 ? "99+" : count}
              </Badge>
            ) : null}
          </Button>
        );
      })}
      <IconTooltip label={t`New space`}>
        <Button
          type="button"
          variant="ghost"
          size="icon-lg"
          aria-label={t`New space`}
          data-testid="space-tabs-new"
          onClick={onCreate}
          className="shrink-0 rounded-full border border-dashed border-foreground/20 text-muted-foreground"
        >
          <Plus size={14} aria-hidden="true" />
        </Button>
      </IconTooltip>
    </nav>
  );
}

/** All / Needs you / Working / Unread over the roster. */
export function RosterFilterChips({
  value,
  onChange,
}: {
  value: RosterFilter;
  onChange: (filter: RosterFilter) => void;
}) {
  const { t } = useLingui();
  const labels: Record<RosterFilter, string> = {
    all: t`All`,
    "needs-you": t`Needs you`,
    working: t`Working`,
    unread: t`Unread`,
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form fieldset.
    <div
      role="group"
      aria-label={t`Filter Nexa Mates`}
      data-testid="roster-filters"
      className="mx-2.5 mb-2 flex shrink-0 flex-wrap gap-1.5"
    >
      {ROSTER_FILTERS.map((filter) => (
        <Button
          key={filter}
          type="button"
          size="chip"
          variant="chip"
          aria-pressed={value === filter}
          data-roster-filter={filter}
          onClick={() => onChange(filter)}
        >
          {labels[filter]}
        </Button>
      ))}
    </div>
  );
}

/** What Needs you and Working say when no chat matches, instead of a blank roster. */
export function RosterFilterEmpty({ filter }: { filter: RosterFilter }) {
  const { t } = useLingui();
  const label = filter === "needs-you" ? t`Nothing needs you` : t`No Nexa Mates working`;
  return (
    <div data-testid="roster-filter-empty" className="px-3 py-4 text-[14px] text-muted-foreground">
      {label}
    </div>
  );
}
